import { getEventsById } from '@eventbrite/event-renderer';
import { StatsigOptions } from '@eventbrite/statsig';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { EVENT_TYPE } from '../../../../constants';
import { DEFAULT_EXPANSIONS } from '../../../../redux/api/expansions';
import { Event } from '../../../../types';

/**
 * Hook which given a list of event IDs, runs
 * a call to search service to get the expanded versions
 *
 * @param {object} props
 */
export interface useBrowseEventExpansionsProps {
    isEmptyBucket: boolean;
    inWindow: boolean;
    allEventsUrl?: string;
    bucketKey?: string;
    bucketWrapperClasses?: string | string[];
    currentPlace?: string;
    containerClasses?: string;
    content?: object[];
    decorationPlacement?: {
        position: string;
        classes: string[];
        parentClasses: string[];
    };
    decorativeImage?: string;
    httpReferer?: string;
    isAuthenticated?: boolean;
    linkUrl?: string;
    locationSlug?: string;
    name?: string;
    onTrackEventClickInternal?: Function;
    template?: string;
    trackLocationChange?: Function;
    trackGAEvent?: Function;
    trackGAEventOnPageUnload?: Function;
    type?: string;
    seeMoreMarkedNoFollow?: boolean;
    isGDPRCountry?: boolean;
    featureFlags?: object;
    isBotRequest?: boolean;
    trackEventClickInternal?: Function;
    seeMore?: object;
    placeId?: string;
    isOnline?: boolean;
    bucketIndex?: number;
    options?: StatsigOptions;
}

export const useBrowseEventExpansions = (
    props: useBrowseEventExpansionsProps,
) => {
    const queryClient = useQueryClient();
    const queryKey = ['browse-buckets', props.placeId, props.isOnline];
    const queryData = queryClient.getQueryData<any>(queryKey);
    const buckets =
        queryData?.pages?.reduce(
            (memo: any, page: { buckets: any }) => [...memo, ...page.buckets],
            [],
        ) || [];

    const currentBucket = buckets?.[props.bucketIndex || 0] || {};
    const shouldExpand = currentBucket && !currentBucket.isContentExpanded;

    const bucketContentIds =
        currentBucket &&
        currentBucket.content &&
        currentBucket.type === EVENT_TYPE
            ? currentBucket.content.map(({ id }: { id: string }) => id)
            : [];

    return useQuery(
        ['expandEvents', bucketContentIds],
        async () => {
            const response = await getEventsById(
                bucketContentIds,
                50,
                DEFAULT_EXPANSIONS,
            );

            queryClient.setQueryData(queryKey, (oldData: any) => {
                //create newBucket with formatted expanded events
                const newBucket = {
                    ...currentBucket,
                    isContentExpanded: true,
                    content: response.events.map((formattedEvent) => ({
                        ...formattedEvent,
                        statsigLocationString: `CityBrowse - ${currentBucket.key}`,
                    })),
                };

                //recover overwritten promoted listings metadata for the promoted events in the bucket
                const updatedBucketContent = updatePromotedMetadataForEvents(
                    currentBucket.content,
                    newBucket.content,
                );
                const updatedNewBucket = {
                    ...newBucket,
                    content: updatedBucketContent,
                };

                //get page that this bucket belongs to
                const pageIndex = oldData.pages.findIndex(
                    (page: { buckets: any[] }) =>
                        page.buckets.some(
                            (bucket: { key: any }) =>
                                bucket.key === currentBucket.key,
                        ),
                );

                //get index of current bucket within page's context
                const bucketIndex = oldData.pages[pageIndex].buckets.findIndex(
                    (bucket: { key: any }) => bucket.key === currentBucket.key,
                );

                //create new buckets array with new data inserted
                const newBuckets = [
                    ...oldData.pages[pageIndex].buckets.slice(0, bucketIndex),
                    updatedNewBucket,
                    ...oldData.pages[pageIndex].buckets.slice(bucketIndex + 1),
                ];

                //create new pages array with page replaced with new buckets
                const newPages = [
                    ...oldData.pages.slice(0, pageIndex),
                    { ...oldData.pages[pageIndex], buckets: newBuckets },
                    ...oldData.pages.slice(pageIndex + 1),
                ];

                return {
                    pageParams: oldData.pageParams,
                    pages: newPages,
                };
            });
        },
        {
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            refetchOnMount: false,
            enabled:
                bucketContentIds.length > 0 &&
                shouldExpand &&
                currentBucket.type === EVENT_TYPE &&
                !props.isEmptyBucket &&
                props.inWindow,
        },
    );
};

const updatePromotedMetadataForEvents = (
    currentBucketContent: any,
    newBucketContent: any,
) => {
    const promotedEvent = currentBucketContent.find(
        (event: Event) => event.promotedListingMetadata,
    );

    if (promotedEvent) {
        const expandedPromotedEventIndex = newBucketContent.findIndexOf(
            (event: Event) => event.id === promotedEvent.id,
        );

        newBucketContent[expandedPromotedEventIndex] = {
            ...newBucketContent[expandedPromotedEventIndex],
            isPromoted: true,
            promotedListingMetadata: promotedEvent.promotedListingMetadata,
        };
    }

    return newBucketContent;
};
